import { modifier } from 'ember-modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Modal from 'tio-ui/components/modal';
import Table from 'tio-ui/components/table';
import Button from 'tio-ui/components/button';
import type { Store as StoreService } from '@warp-drive/core';
import type { EligibilityFileJobLogExtension as EligibilityFileJobLogModel } from 'tio-data/data/resources/eligibility-file-job-log.ext';
import type {
  MetaDiffEntry,
  MetaDiffMeta,
} from 'tio-data/data/resources/eligibility-file-job-log.type';
import { fetchEligibilityMetaDiffRequest } from 'tio-data/requests/eligibility-file-records';

interface TopChangesModalSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    jobLogId: string | null;
  };
}

type MetaDiffResponse = { data?: MetaDiffEntry[]; meta?: MetaDiffMeta };

export default class TopChangesModal extends Component<TopChangesModalSignature> {
  @service declare store: StoreService;

  @tracked isLoading = false;
  @tracked loadFailed = false;
  @tracked metaDiff: MetaDiffEntry[] = [];
  @tracked metaDiffMeta: MetaDiffMeta | null = null;
  private hasLoaded = false;
  private abortController: AbortController | null = null;

  private triggerLoad = modifier(() => {
    if (!this.hasLoaded && this.args.jobLogId) {
      this.hasLoaded = true;
      this.loadMetaDiff();
    }
  });

  loadMetaDiff = async () => {
    const jobLogId = this.args.jobLogId;
    if (!jobLogId) {
      return;
    }

    // Fast path: if the preprocessor already pre-computed meta_diff and the
    // job log model is cached in the store, render directly without hitting
    // the proxy endpoint.
    const cached = this.store.peekRecord(
      'eligibility-file-job-log',
      jobLogId
    ) as EligibilityFileJobLogModel | null;
    const cachedMetaDiff = cached?.metaDiff;
    if (cachedMetaDiff && Array.isArray(cachedMetaDiff.data)) {
      this.metaDiff = cachedMetaDiff.data.slice(0, 20);
      this.metaDiffMeta = cachedMetaDiff.meta ?? null;
      this.loadFailed = false;
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.loadFailed = false;
    this.metaDiff = [];
    this.metaDiffMeta = null;

    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const { content: response } = (await this.store.request(
        fetchEligibilityMetaDiffRequest(jobLogId, this.abortController.signal)
      )) as { content: MetaDiffResponse };

      this.metaDiff = (response.data || []).slice(0, 20);
      this.metaDiffMeta = response.meta || null;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        return;
      }
      this.metaDiff = [];
      this.metaDiffMeta = null;
      this.loadFailed = true;
    } finally {
      this.isLoading = false;
    }
  };

  handleClose = () => {
    this.abortController?.abort();
    this.loadFailed = false;
    this.isLoading = false;
    this.hasLoaded = false;
    this.args.onClose();
  };

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{this.handleClose}}
      @ariaLabel={{t "eligibility_files.meta_diff.title"}}
    >
      <:header>
        <span {{this.triggerLoad}}>{{t "eligibility_files.meta_diff.title"}}</span>
      </:header>
      <:body>
        {{#if this.isLoading}}
          <Table @isLoading={{true}} @previewRowLimit={{3}} @previewColspan={{2}}>
            <:thead as |head|>
              <head.tr>
                <head.th>{{t "eligibility_files.meta_diff.field"}}</head.th>
                <head.th>{{t "eligibility_files.meta_diff.changes_detected"}}</head.th>
              </head.tr>
            </:thead>
            <:tbody as |body|>
              <body.tr>
                <body.td>-</body.td>
                <body.td>-</body.td>
              </body.tr>
            </:tbody>
          </Table>
        {{else}}
          {{#if this.metaDiffMeta}}
            <div class="tio-flex-loose mb-4 text-sm tio-fg-secondary">
              <span>
                {{t "eligibility_files.meta_diff.common_records"}}:
                <strong>{{this.metaDiffMeta.common_records}}</strong>
              </span>
              <span>
                {{t "eligibility_files.meta_diff.only_in_previous"}}:
                <strong>{{this.metaDiffMeta.records_only_in_previous}}</strong>
              </span>
              <span>
                {{t "eligibility_files.meta_diff.only_in_current"}}:
                <strong>{{this.metaDiffMeta.records_only_in_current}}</strong>
              </span>
            </div>
          {{/if}}
          {{#if this.metaDiff.length}}
            <Table>
              <:thead as |head|>
                <head.tr>
                  <head.th>{{t "eligibility_files.meta_diff.field"}}</head.th>
                  <head.th>{{t "eligibility_files.meta_diff.changes_detected"}}</head.th>
                </head.tr>
              </:thead>
              <:tbody as |body|>
                {{#each this.metaDiff as |entry|}}
                  <body.tr>
                    <body.td>{{entry.field}}</body.td>
                    <body.td>{{entry.count}}</body.td>
                  </body.tr>
                {{/each}}
              </:tbody>
            </Table>
          {{else}}
            <p
              class="text-sm tio-fg-muted"
              data-test-meta-diff-message={{if this.loadFailed "error" "empty"}}
            >
              {{#if this.loadFailed}}
                {{t "eligibility_files.meta_diff.load_error"}}
              {{else}}
                {{t "eligibility_files.meta_diff.no_changes"}}
              {{/if}}
            </p>
          {{/if}}
        {{/if}}
      </:body>
      <:footer>
        <div class="tio-flex-end">
          <Button @appearance="outlined" {{on "click" this.handleClose}}>
            {{t "close"}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
